import { h } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import { track } from "utils/tracking";
import { accessNested } from "utils/utils";

import ChangeRegionModal from 'components/shared/regionMismatchModal/changeRegionModal';


/* State Getters
--------------------------------*/

const getGeoCountryCode = state => state.client.geo && state.client.geo.countryCode;
const getAppRegionCode = state => state.appData.meta.region.slug;
const getAppLanguageCode = state => state.appData.meta.region.language;

const getAppCountryCode = state => {
	// Grab the first 2 letters of the region code
	// (ex. BE_FR => BE)
	const match = /^\w{2}/.exec(getAppRegionCode(state));
	return match && match[0];
};

const getAppGeoRegions = state => {
	const alteredRegions = {};
	const dynamicRegions = accessNested(state, "dynamicData.regions");
	const staticRegions = accessNested(state, "appData.meta.regions", {});
	const extraExternalRegions = accessNested(state, "appData.meta.extraRegions", null);
	const regions = dynamicRegions || staticRegions;

	// Add in extra external regions
	Object.keys(regions).forEach((reg)=>{
		// Add in the regionCode to all of the regions
		alteredRegions[reg] = {
			...regions[reg],
			regionCode: reg
		};
	});

	if (extraExternalRegions) {
		// format the regions to the right format and add them to the regions
		Object.keys(extraExternalRegions).forEach((key)=>{

			alteredRegions[key] = {
				regionCode: key,
				url: extraExternalRegions[key]
			};
		});
	}

	return alteredRegions;
};



/* Helper Functions
-----------------------------*/

function getGeoRegionMatches(countryCode, regionCodes) {
	// Create reg ex to match all regions which match country
	// e.g. for ch could be: ch, ch_fr
	// e.g. for be could be: be_fr, be_nl
	const countryCodeRE = new RegExp(`^(${countryCode})(_[a-z]{2})?$`);

	return regionCodes
		.filter(region => countryCodeRE.exec(region))
		.map(region => {
			const regexResult = countryCodeRE.exec(region);
			return {
				regionCode: region,
				countryCode: regexResult[1],
				languageCode: regexResult[2]
			};
		});
}

function getCurrentAppLanguageMatch(country, language, regionMatches) {
	return regionMatches.find(match => {
		return match.countryCode === country && match.languageCode === language;
	});
}


/**
 * Detect if the Users region is not the same as the apps region
 * @return {Boolean}
 */
function detectRegionMismatch(store) {
	// If not first visit, then shouldn't display modal
	// (as user has previously decided to stay here)
	// TODO: add in again
	// if (!isFirstVisit(this.props)) return false;

	const appCode = getAppCountryCode(store);
	const appLanguage = getAppLanguageCode(store);
	const appRegion = getAppRegionCode(store);
	const geoCode = getGeoCountryCode(store);

	// TODO: also check language in the browser presets
	// needed for country's with multiple languages (ex. Belgium)


	// If geo and app region not determined then don't display modal
	// (as we don't yet know if we have a mismatch)
	if (!(appCode !== null && geoCode !== null)) return false;

	let geoMismatch = appCode !== geoCode;

	if (appRegion.indexOf('_') > -1 && navigator && navigator.language && appLanguage !== navigator.language) {
		// Means it's a multi language app,
		// so let's check if the user has a language set
		geoMismatch = true;
	}

	if (geoMismatch) {
		// The regions aren't matching, so let the tracking know
		track({ a: "geo-region-mismatch", once: true });
	} else {
		// regions are matching
		track({ a: "geo-region-match", once: true });
	}

	return geoMismatch;
}


function getMatchingRegionApp(state) {
	// Get geo countryCode, app geo regions and app langugage from state
	const geoCountryCode = getGeoCountryCode(state);
	const availableAppRegions = getAppGeoRegions(state);
	const appRegion = getAppRegionCode(state);
	const appLanguageCode = getAppLanguageCode(state);

	// Find all app regions which match the current country code
	const matches = getGeoRegionMatches(geoCountryCode, Object.keys(availableAppRegions));

	// console.log('the availavle regions', availableAppRegions);

	// Get all allowable geo region / app region matches
	const currentAppLanguageMatch = getCurrentAppLanguageMatch(
		geoCountryCode,
		appLanguageCode,
		matches
	);

	// console.log('get Matching region App',  currentAppLanguageMatch, state.appData.meta, appLanguageCode, availableAppRegions, geoCountryCode);

	if (currentAppLanguageMatch) {
		// First try to match against language of current region (NOTE: is this the right logic?)
		return availableAppRegions[currentAppLanguageMatch.regionCode];
	}


	// Then try to match against exact region match
	const exactMatch = 	matches.find(({regionCode}) => regionCode === geoCountryCode);

	if (exactMatch) {
		return availableAppRegions[exactMatch.regionCode];
	} else if (availableAppRegions['other']) {
		return availableAppRegions['other'];
	}


	// Then return first result found
	const firstMatch = matches[0];

	if (firstMatch) {
		if (appRegion.indexOf('_') > 0) {
			// Multi Region App
			if (navigator && navigator.language && availableAppRegions[geoCountryCode+'_'+navigator.language]) {
				// Check the language the user has set in the browser
				// and see if we have an app available for that language in that region
				return availableAppRegions[geoCountryCode+'_'+navigator.language];
			}
			// Didn't find any region app with that language,
			// so let's not show the popup
		} else {
			return availableAppRegions[firstMatch.regionCode];
		}
	}
	// Return null if no match
	return null;
}

function displayRegionModal() {
	const store = Store.get();

	const shouldDisplayRegionMismatchModal =
		store.appData.options.useRegionMismatchModal && // Is it enabled in the appData
		detectRegionMismatch(store); // Are we actually in the wrong region

	if (!shouldDisplayRegionMismatchModal) {
		return;
	}

	const userRegionApp = getMatchingRegionApp(store);
	if (!userRegionApp) {
		// No App found with the users region,
		// so don't show the popup
		return;
	}

	const appRegionCode  = getAppRegionCode(store);
	// const appCountryCode = getAppCountryCode(store);
	let currentApp = getAppGeoRegions(store)[appRegionCode];
	if (!currentApp) {
		currentApp = {
			regionCode: appRegionCode,
			url: store.appData.meta.url
		};
	}

	// Display the region modal
	Store.emit(actions.SHOW_MODAL, (
		<ChangeRegionModal
			geo={store.client.geo}
			appRegionCode={getAppRegionCode(store)}
			appCountryCode={getAppCountryCode(store)}
			imagePath={store.appData.urls.roots.sharedImages}
			apiRoot={store.appData.apis.stdata.root}
			currentApp={currentApp}
			userRegionApp={userRegionApp}
			copy={store.appData.copy}
		/>
	), null, { type: 'regionModal' });
}


/* Exported Functions
--------------------------------*/

function displayRegionModalIfNeeded() {
	let store = Store.get();

	if (store.user && store.user.regionModalDismissed) {
		// The user has dismissed the modal before,
		// so let's not show again
		return;
	}

	// Make sure the call to get dynamic content is finished
	if (!store.dynamicData || !store.dynamicData.regions) {
		// Listen to when dynamic data is updated
		Store.on(actions.UPDATE_DYNAMIC_DATA, () => {
			if (Store.get().dynamicData.regions) {
				// Regional data is finally available,
				// so proceed
				displayRegionModal();
			}
		});
	} else {
		// All the data has already been loaded,
		// so proceed
		displayRegionModal();
	}
}


export {
	displayRegionModalIfNeeded
};
